import { motion } from "framer-motion";
import { Scale, FileText, AlertTriangle, Shield, Clock, CreditCard } from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const termsContent = [
  {
    icon: <Scale />,
    title: "1. Terms of Use",
    content: `By accessing and using PromptPerfect, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, you must not use our service.`
  },
  {
    icon: <FileText />,
    title: "2. Service Description",
    content: `PromptPerfect provides AI prompt optimization and refinement services. We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.`
  },
  {
    icon: <Shield />,
    title: "3. User Obligations",
    content: `You agree to:
    • Provide accurate account information
    • Maintain the security of your account
    • Comply with all applicable laws
    • Not misuse our services
    • Not infringe on others' intellectual property`
  },
  {
    icon: <CreditCard />,
    title: "4. Payment Terms",
    content: `• Subscription fees are billed in advance
    • All payments are non-refundable
    • We may change pricing with 30 days notice
    • Failed payments may result in service suspension`
  },
  {
    icon: <AlertTriangle />,
    title: "5. Limitations of Liability",
    content: `• Services are provided "as is"
    • We are not liable for any indirect damages
    • Our liability is limited to the amount paid
    • We do not guarantee specific results`
  },
  {
    icon: <Clock />,
    title: "6. Term and Termination",
    content: `• These terms remain in effect while you use our service
    • We may terminate your access for violations
    • You may cancel your subscription at any time
    • Some obligations survive termination`
  }
];

const Terms = () => {
  const lastUpdated = "March 15, 2024";

  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Terms of Service"
        description="Please read these terms carefully before using PromptPerfect."
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-16">
          <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
            These Terms of Service ("Terms") govern your access to and use of PromptPerfect's website, products, and services. These Terms constitute a legally binding agreement between you and PromptPerfect. By using our services, you agree to these Terms.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {termsContent.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card p-6 sm:p-8 rounded-xl shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  {section.icon}
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground">{section.title}</h2>
              </div>
              <div className="text-muted-foreground whitespace-pre-line text-sm sm:text-base">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Terms */}
        <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">7. Intellectual Property</h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              All content, features, and functionality of PromptPerfect, including but not limited to text, graphics, logos, button icons, images, audio clips, digital downloads, data compilations, and software, are the exclusive property of PromptPerfect or its licensors and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">8. Governing Law</h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction], without regard to its conflict of law provisions. Any disputes relating to these terms will be subject to the exclusive jurisdiction of the courts of [Jurisdiction].
            </p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">9. Changes to Terms</h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the new Terms on this page and updating the "last updated" date. Your continued use of the Service after such modifications constitutes your acceptance of the new Terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Contact Us</h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              If you have any questions about these Terms, please contact us at:
              <br className="hidden sm:block" />
              Email: legal@promptperfect.ai
              <br className="hidden sm:block" />
              Address: 123 AI Street, Tech City, TC 12345
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms; 